/**
 * Owner Registration Page
 *
 * Multi-step registration flow for new organization owners.
 * Step 1: Choose authentication method (Google recommended, or email/password)
 * Step 2: Profile information (if email path)
 * Step 3: Payment (Stripe Checkout redirect)
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { useTranslation } from '../../hooks';
import { graphqlMutation } from '@business-assistant/shared/database/graphqlClient';
import { SectorSelector, type Sector } from './SectorSelector';

// =============================================================================
// Types
// =============================================================================

type RegistrationStep = 'method' | 'profile' | 'sector' | 'plan' | 'processing';

interface FormData {
  email: string;
  password: string;
  confirmPassword: string;
  displayName: string;
  firstName: string;
  lastName: string;
  companyName: string;
  sectorId: string | null;
  plan: 'starter' | 'professional' | 'enterprise';
}

// =============================================================================
// GraphQL
// =============================================================================

const REGISTER_OWNER_MUTATION = `
  mutation RegisterOwner($input: RegisterOwnerInput!) {
    registerOwner(input: $input) {
      success
      userId
      organizationId
      checkoutUrl
      error
    }
  }
`;

// =============================================================================
// Component
// =============================================================================

export function OwnerRegistrationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState<RegistrationStep>('method');
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    firstName: '',
    lastName: '',
    companyName: '',
    sectorId: null,
    plan: 'starter',
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Google OAuth login
  const googleLogin = useGoogleLogin({
    onSuccess: async (response) => {
      // For owner registration with Google, we still need to collect sector info
      // and redirect to payment
      setFormData(prev => ({
        ...prev,
        // We'll get user info from the ID token on backend
      }));
      setStep('sector');
    },
    onError: () => {
      setError(t('auth.googleLoginFailed', 'Google login failed. Please try again.'));
    },
    flow: 'implicit',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleMethodSelect = (method: 'google' | 'email') => {
    if (method === 'google') {
      googleLogin();
    } else {
      setStep('profile');
    }
  };

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate
    if (!formData.email || !formData.password || !formData.displayName) {
      setError(t('auth.fillRequiredFields', 'Please fill in all required fields'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordsMismatch', 'Passwords do not match'));
      return;
    }

    if (formData.password.length < 8) {
      setError(t('auth.passwordTooShort', 'Password must be at least 8 characters'));
      return;
    }

    setStep('sector');
  };

  // Handle sector selection
  const handleSectorSelect = (sectorId: string) => {
    setFormData(prev => ({ ...prev, sectorId }));
  };

  const handleSectorContinue = () => {
    if (!formData.sectorId) {
      setError(t('auth.selectSector', 'Please select your business sector'));
      return;
    }
    setStep('plan');
  };

  // Create new sector via API
  const handleCreateSector = async (name: string, description: string): Promise<Sector> => {
    const response = await fetch('/api/memory/sectors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name,
        description,
        keywords: name.toLowerCase().split(' '),
        isActive: true,
      }),
    });
    const data = await response.json();
    if (!data.success) throw new Error('Failed to create sector');
    return data.data;
  };

  const handlePlanSelect = async (plan: 'starter' | 'professional' | 'enterprise') => {
    setFormData(prev => ({ ...prev, plan }));
    setLoading(true);
    setError(null);
    setStep('processing');

    try {
      const response = await graphqlMutation<{
        registerOwner: {
          success: boolean;
          userId?: string;
          organizationId?: string;
          checkoutUrl?: string;
          error?: string;
        };
      }>(REGISTER_OWNER_MUTATION, {
        input: {
          email: formData.email,
          password: formData.password,
          displayName: formData.displayName,
          firstName: formData.firstName || null,
          lastName: formData.lastName || null,
          companyName: formData.companyName || null,
          sectorId: formData.sectorId,
          plan,
        },
      });

      const { registerOwner } = response;

      if (!registerOwner.success) {
        throw new Error(registerOwner.error || 'Registration failed');
      }

      if (registerOwner.checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = registerOwner.checkoutUrl;
      } else {
        // No payment required (unlikely for owner registration)
        navigate('/login?registered=true');
      }
    } catch (err) {
      console.error('[OwnerRegistration] Error:', err);
      setError(err instanceof Error ? err.message : 'Registration failed');
      setStep('plan');
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      backgroundColor: 'var(--bg-secondary, #f9fafb)',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '480px',
        backgroundColor: 'var(--bg-primary, #ffffff)',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        padding: '2rem',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{
            margin: 0,
            fontSize: '1.75rem',
            fontWeight: 700,
            color: 'var(--text-primary, #111827)',
          }}>
            {t('auth.createOrganization', 'Create Your Organization')}
          </h1>
          <p style={{
            margin: '0.5rem 0 0',
            color: 'var(--text-muted, #6b7280)',
          }}>
            {step === 'method' && t('auth.chooseMethod', 'Choose how to get started')}
            {step === 'profile' && t('auth.enterDetails', 'Enter your details')}
            {step === 'sector' && t('auth.selectYourSector', 'What type of business do you have?')}
            {step === 'plan' && t('auth.choosePlan', 'Choose your plan')}
            {step === 'processing' && t('auth.processing', 'Setting up your account...')}
          </p>
        </div>

        {/* Step indicator */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '0.5rem',
          marginBottom: '2rem',
        }}>
          {['method', 'profile', 'sector', 'plan'].map((s, i) => (
            <div
              key={s}
              style={{
                width: '2rem',
                height: '0.25rem',
                borderRadius: '0.125rem',
                backgroundColor: ['method', 'profile', 'sector', 'plan'].indexOf(step) >= i
                  ? 'var(--primary, #3b82f6)'
                  : 'var(--border, #e5e7eb)',
              }}
            />
          ))}
        </div>

        {error && (
          <div style={{
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            backgroundColor: 'var(--error-bg, #fef2f2)',
            color: 'var(--error, #ef4444)',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
          }}>
            {error}
          </div>
        )}

        {/* Step: Method Selection */}
        {step === 'method' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <button
              onClick={() => handleMethodSelect('google')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                padding: '1rem',
                backgroundColor: '#ffffff',
                border: '2px solid var(--primary, #3b82f6)',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('auth.continueWithGoogle', 'Continue with Google')}
            </button>

            <p style={{
              textAlign: 'center',
              color: 'var(--success, #22c55e)',
              fontSize: '0.75rem',
              margin: 0,
            }}>
              {t('auth.googleBenefits', 'Recommended: Enables Drive, Calendar & Meet integration')}
            </p>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              margin: '0.5rem 0',
            }}>
              <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border, #e5e7eb)' }} />
              <span style={{ color: 'var(--text-muted, #6b7280)', fontSize: '0.875rem' }}>
                {t('common.or', 'or')}
              </span>
              <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border, #e5e7eb)' }} />
            </div>

            <button
              onClick={() => handleMethodSelect('email')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                padding: '1rem',
                backgroundColor: 'var(--bg-secondary, #f3f4f6)',
                border: '1px solid var(--border, #e5e7eb)',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'var(--text-primary, #111827)',
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
              {t('auth.useEmailPassword', 'Use Email & Password')}
            </button>

            <p style={{
              textAlign: 'center',
              color: 'var(--text-muted, #6b7280)',
              fontSize: '0.75rem',
              margin: 0,
            }}>
              {t('auth.emailLimitations', 'Limited features: No Google integrations')}
            </p>
          </div>
        )}

        {/* Step: Profile Information */}
        {step === 'profile' && (
          <form onSubmit={handleProfileSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
                {t('auth.email', 'Email')} *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--border, #e5e7eb)',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
                {t('auth.displayName', 'Display Name')} *
              </label>
              <input
                type="text"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--border, #e5e7eb)',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
                  {t('auth.firstName', 'First Name')}
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--border, #e5e7eb)',
                    borderRadius: '0.5rem',
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
                  {t('auth.lastName', 'Last Name')}
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--border, #e5e7eb)',
                    borderRadius: '0.5rem',
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
                {t('auth.companyName', 'Company/Organization Name')}
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--border, #e5e7eb)',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
                {t('auth.password', 'Password')} *
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                minLength={8}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--border, #e5e7eb)',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
                {t('auth.confirmPassword', 'Confirm Password')} *
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--border, #e5e7eb)',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
              <button
                type="button"
                onClick={() => setStep('method')}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  backgroundColor: 'var(--bg-secondary, #f3f4f6)',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 500,
                }}
              >
                {t('common.back', 'Back')}
              </button>
              <button
                type="submit"
                style={{
                  flex: 2,
                  padding: '0.75rem',
                  backgroundColor: 'var(--primary, #3b82f6)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 500,
                }}
              >
                {t('common.continue', 'Continue')}
              </button>
            </div>
          </form>
        )}

        {/* Step: Sector Selection */}
        {step === 'sector' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <p style={{
              margin: 0,
              fontSize: '0.875rem',
              color: 'var(--text-muted)',
              textAlign: 'center',
            }}>
              {t('auth.sectorHelp', 'This helps Marta understand your business and provide better assistance')}
            </p>

            <SectorSelector
              selectedSectorId={formData.sectorId}
              onSelect={handleSectorSelect}
              onCreateNew={handleCreateSector}
            />

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
              <button
                onClick={() => setStep('profile')}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  backgroundColor: 'transparent',
                  border: '1px solid var(--border)',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  color: 'var(--text-muted)',
                }}
              >
                {t('common.back', 'Back')}
              </button>
              <button
                onClick={handleSectorContinue}
                disabled={!formData.sectorId}
                style={{
                  flex: 2,
                  padding: '0.75rem',
                  backgroundColor: formData.sectorId ? 'var(--primary)' : 'var(--border)',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: formData.sectorId ? 'pointer' : 'not-allowed',
                  fontSize: '0.875rem',
                  color: 'white',
                  fontWeight: 500,
                }}
              >
                {t('common.continue', 'Continue')}
              </button>
            </div>
          </div>
        )}

        {/* Step: Plan Selection */}
        {step === 'plan' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Starter Plan */}
            <button
              onClick={() => handlePlanSelect('starter')}
              disabled={loading}
              style={{
                padding: '1.25rem',
                backgroundColor: '#ffffff',
                border: '2px solid var(--border, #e5e7eb)',
                borderRadius: '0.75rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: '1.125rem' }}>Starter</span>
                <span style={{ color: 'var(--text-muted, #6b7280)' }}>$19/mo</span>
              </div>
              <p style={{ margin: '0.5rem 0 0', color: 'var(--text-muted, #6b7280)', fontSize: '0.875rem' }}>
                {t('plans.starterDesc', 'Basic features for small teams')}
              </p>
            </button>

            {/* Professional Plan */}
            <button
              onClick={() => handlePlanSelect('professional')}
              disabled={loading}
              style={{
                padding: '1.25rem',
                backgroundColor: 'var(--primary-bg, #eff6ff)',
                border: '2px solid var(--primary, #3b82f6)',
                borderRadius: '0.75rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: '1.125rem' }}>
                  Professional
                  <span style={{
                    marginLeft: '0.5rem',
                    padding: '0.125rem 0.5rem',
                    backgroundColor: 'var(--primary, #3b82f6)',
                    color: '#ffffff',
                    borderRadius: '0.25rem',
                    fontSize: '0.625rem',
                    fontWeight: 500,
                  }}>
                    {t('plans.popular', 'POPULAR')}
                  </span>
                </span>
                <span style={{ color: 'var(--text-muted, #6b7280)' }}>$49/mo</span>
              </div>
              <p style={{ margin: '0.5rem 0 0', color: 'var(--text-muted, #6b7280)', fontSize: '0.875rem' }}>
                {t('plans.professionalDesc', 'Full features + Google integrations')}
              </p>
            </button>

            {/* Enterprise Plan */}
            <button
              onClick={() => handlePlanSelect('enterprise')}
              disabled={loading}
              style={{
                padding: '1.25rem',
                backgroundColor: '#ffffff',
                border: '2px solid var(--border, #e5e7eb)',
                borderRadius: '0.75rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: '1.125rem' }}>Enterprise</span>
                <span style={{ color: 'var(--text-muted, #6b7280)' }}>{t('plans.contactUs', 'Contact us')}</span>
              </div>
              <p style={{ margin: '0.5rem 0 0', color: 'var(--text-muted, #6b7280)', fontSize: '0.875rem' }}>
                {t('plans.enterpriseDesc', 'Custom solutions for large organizations')}
              </p>
            </button>

            <button
              onClick={() => setStep('profile')}
              disabled={loading}
              style={{
                marginTop: '0.5rem',
                padding: '0.75rem',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                color: 'var(--text-muted, #6b7280)',
              }}
            >
              {t('common.back', 'Back')}
            </button>
          </div>
        )}

        {/* Step: Processing */}
        {step === 'processing' && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{
              width: '48px',
              height: '48px',
              margin: '0 auto 1rem',
              border: '4px solid var(--border, #e5e7eb)',
              borderTopColor: 'var(--primary, #3b82f6)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
            <p style={{ color: 'var(--text-muted, #6b7280)' }}>
              {t('auth.settingUpAccount', 'Setting up your account...')}
            </p>
            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}

        {/* Footer */}
        <div style={{
          marginTop: '2rem',
          paddingTop: '1.5rem',
          borderTop: '1px solid var(--border, #e5e7eb)',
          textAlign: 'center',
        }}>
          <p style={{ margin: 0, color: 'var(--text-muted, #6b7280)', fontSize: '0.875rem' }}>
            {t('auth.alreadyHaveAccount', 'Already have an account?')}{' '}
            <Link to="/login" style={{ color: 'var(--primary, #3b82f6)', textDecoration: 'none' }}>
              {t('auth.signIn', 'Sign in')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default OwnerRegistrationPage;
